import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import {
  LEVEL_COLORS,
  CHART_HEIGHT,
  CHART_STYLES,
  CHART_STYLES_BOTTOM_ROTATED,
  BAR_ANIMATION_DURATION,
} from '../../../utils/constants';
import { getColorForPercent } from './peakPersonalizationHelpers';
import styles from './PeakPersonalizationChart.scss';

export interface PeakPersonalizationChartProps {
  data: number[];
  labels: string[];
  onDataChange: (index: number, value: number) => void;
  instruction: string;
  onClearAll: () => void;
  scale?: { min: number; max: number };
  valueLevels?: number[];
  labelRotation?: number;
  disabledBars?: string[];
  disabled?: boolean;
}

export const PeakPersonalizationChart = ({
  data,
  labels,
  onDataChange,
  instruction,
  onClearAll,
  scale = { min: 0, max: 100 },
  valueLevels,
  labelRotation = 0,
  disabledBars = [],
  disabled = false,
}: PeakPersonalizationChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const [dimensions, setDimensions] = useState({ width: 0, innerWidth: 0 });

  const disabledSet = useMemo(() => new Set(disabledBars), [disabledBars]);
  const scaleRange = scale.max - scale.min;
  const bottomMargin = labelRotation
    ? CHART_STYLES_BOTTOM_ROTATED
    : CHART_STYLES.bottom;
  const innerHeight = CHART_HEIGHT - CHART_STYLES.top - bottomMargin;

  const getBarLayout = useCallback(
    (width: number) => {
      const innerWidth = width - CHART_STYLES.left - CHART_STYLES.right;
      const n = labels.length;
      const step = n > 0 ? innerWidth / n : 0;
      const bandwidth = step;

      return {
        innerWidth,
        bandwidth,
        step,
        getBarX: (i: number) => CHART_STYLES.left + i * step,
        getBarCenter: (i: number) => CHART_STYLES.left + (i + 0.5) * step,
      };
    },
    [labels.length],
  );

  const updateDimensions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const width = container.clientWidth;
    const innerWidth = width - CHART_STYLES.borderWidth;
    setDimensions({ width, innerWidth });
  }, []);

  useEffect(() => {
    updateDimensions();
    const container = containerRef.current;
    if (!container) return;
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [updateDimensions]);

  const getBarHeight = useCallback(
    (d: number, i: number) => {
      if (disabledSet.has(labels[i])) return 0;
      if (d <= scale.min) return 0;
      const yScale = (val: number) =>
        scaleRange > 0
          ? innerHeight - ((val - scale.min) / scaleRange) * innerHeight
          : innerHeight;
      return innerHeight - yScale(d);
    },
    [disabledSet, labels, scale, scaleRange, innerHeight],
  );

  const chartAreaTopOffset = CHART_STYLES.top + CHART_STYLES.top;

  const getPositionFromMouse = useCallback(
    (
      clientX: number,
      clientY: number,
    ): { index: number; value: number } | null => {
      const container = containerRef.current;
      if (!container) return null;
      const rect = container.getBoundingClientRect();
      const x = clientX - rect.left - CHART_STYLES.left;
      const y = clientY - rect.top - chartAreaTopOffset;
      const layout = getBarLayout(container.clientWidth);
      const { step, bandwidth } = layout;

      let index: number | null = null;
      for (let i = 0; i < labels.length; i++) {
        const bandStart = i * step;
        const bandEnd = bandStart + bandwidth;
        if (x >= bandStart && x <= bandEnd) {
          index = i;
          break;
        }
      }
      if (index === null) return null;
      if (disabledSet.has(labels[index])) return null;
      const normalizedHeight = Math.max(0, Math.min(1, 1 - y / innerHeight));
      let value =
        normalizedHeight >= 0.95
          ? scale.max
          : scale.min + normalizedHeight * scaleRange;
      if (valueLevels && valueLevels.length > 0) {
        const nearest = valueLevels.reduce((prev, curr) =>
          Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
        );
        value = nearest;
      }
      return { index, value };
    },
    [
      labels,
      disabledSet,
      scale,
      scaleRange,
      valueLevels,
      getBarLayout,
      innerHeight,
    ],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || e.button !== 0) return;
      const result = getPositionFromMouse(e.clientX, e.clientY);
      if (result !== null) {
        isDraggingRef.current = true;
        onDataChange(result.index, result.value);
      }
    },
    [disabled, getPositionFromMouse, onDataChange],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDraggingRef.current) return;
      const result = getPositionFromMouse(e.clientX, e.clientY);
      if (result !== null) {
        onDataChange(result.index, result.value);
      }
    },
    [getPositionFromMouse, onDataChange],
  );

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return;
      const touch = e.touches[0];
      if (!touch) return;
      const result = getPositionFromMouse(touch.clientX, touch.clientY);
      if (result !== null) {
        isDraggingRef.current = true;
        onDataChange(result.index, result.value);
      }
    },
    [disabled, getPositionFromMouse, onDataChange],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const result = getPositionFromMouse(touch.clientX, touch.clientY);
      if (result !== null) {
        onDataChange(result.index, result.value);
      }
    },
    [getPositionFromMouse, onDataChange],
  );

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      isDraggingRef.current = false;
    };
    const handleGlobalTouchEnd = () => {
      isDraggingRef.current = false;
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalTouchEnd);
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, []);

  const layout = getBarLayout(dimensions.width);
  const hasDimensions = dimensions.width > 0;

  return (
    <div className={styles.chartSection}>
      <p className={styles.instruction}>{instruction}</p>
      <div
        ref={containerRef}
        className={styles.chartContainer}
        role="application"
        aria-label="Busy level chart - click and drag to set values"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div
          className={styles.labelsRow}
          style={{
            width: layout.innerWidth,
            height: CHART_STYLES.top,
            position: 'relative',
          }}
        >
          {labels.map((label) => (
            <div
              key={label}
              className={`${styles.label} ${disabledSet.has(label) ? styles.labelDisabled : ''}`}
            >
              {label}
            </div>
          ))}
        </div>
        {hasDimensions && (
          <div
            className={styles.chartContent}
            style={{
              paddingLeft: CHART_STYLES.left,
              paddingTop: CHART_STYLES.top,
            }}
          >
            <div
              className={cx(styles.barsRow, styles.barsRowMaxWidth)}
              style={{
                width: layout.innerWidth,
                height: innerHeight,
              }}
            >
              {data.map((_, i) => {
                const d = data[i];
                const barHeight = getBarHeight(d, i);
                const isDisabled = disabledSet.has(labels[i]);
                const normalizedPercent =
                  scaleRange > 0 ? ((d - scale.min) / scaleRange) * 100 : 0;
                const showTopBorder: number = isDisabled
                  ? 0
                  : normalizedPercent > 2 && normalizedPercent <= 95
                    ? 1
                    : normalizedPercent > 95
                      ? 0
                      : 0.25;
                const barFill = getColorForPercent(normalizedPercent);
                const barOpacity = d <= scale.min ? 0 : 1;

                return (
                  <div key={labels[i]} className={styles.barColumn}>
                    <div
                      className={styles.barDisabledBg}
                      style={{
                        backgroundColor: isDisabled ? '#121620' : 'transparent',
                        cursor: isDisabled ? 'no-drop' : 'pointer',
                      }}
                    />
                    <div className={styles.barWrapper}>
                      <div
                        className={styles.bar}
                        style={{
                          height: barHeight,
                          backgroundColor: barFill,
                          opacity: barOpacity,
                          transition: `height ${BAR_ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1), opacity ${BAR_ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1)`,
                        }}
                      />
                      <div
                        className={styles.barTopBorder}
                        style={{
                          bottom: barHeight,
                          opacity: showTopBorder,
                          transition: `bottom ${BAR_ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1), opacity ${BAR_ANIMATION_DURATION}ms cubic-bezier(0.33, 1, 0.68, 1)`,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <div className={styles.chartFooter}>
        <div className={styles.legend}>
          {LEVEL_COLORS.map((level) => (
            <div key={level.label} className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{
                  backgroundColor: level.color,
                }}
              />
              <span className={styles.legendLabel}>{level.label}</span>
            </div>
          ))}
        </div>
        <button
          type="button"
          className={styles.clearAll}
          onClick={onClearAll}
          disabled={disabled}
        >
          Clear all
        </button>
      </div>
    </div>
  );
};
